<template>
  <SheetContent>
    <SheetHeader padded class="!p-2 !px-4 sm:!p-4 sm:!px-10">
      <SheetTitle size="lg" class="!text-base sm:!text-2xl">Send Message to {{ props.name }}</SheetTitle>
    </SheetHeader>

    <Separator />

    <SheetBody padded class="flex-1 min-h-0 overflow-y-auto">
      <ChatHistory :contact-id="props.id" :is-loading="isLoading" :contact-messages="contactMessages" class="h-full" />
    </SheetBody>

    <Separator />
    <SheetBody padded class="shrink-0">
      <template v-if="props.canReceiveSms">
        <ChatInput v-model="inputMessage" v-model:invoke-on="invokeOn" :is-scheduled="isScheduled" />
        <div v-if="isScheduled" class="flex items-center justify-between gap-4">
          <Button class="w-1/2 mt-3 sm:mt-6" @click="isScheduled = false">Go back</Button>
          <Button
            class="w-1/2 mt-3 sm:mt-6"
            :disabled="!inputMessage || !invokeOn"
            type="button"
            @click="scheduleMessage"
          >
            Schedule
            <LocalLoadingSpinner />
          </Button>
        </div>
        <div v-else class="flex items-center justify-between gap-4">
          <Button class="w-1/2 mt-3 sm:mt-6" :disabled="!inputMessage" type="button" @click="sendMessage">Send</Button>
          <Button class="w-1/2 mt-3 sm:mt-6" type="button" @click="isScheduled = true">Schedule for later</Button>
        </div>
      </template>
      <template v-else>
        <Alert variant="info" class="flex gap-2 items-center">
          <vue-feather type="info" class="h-4 w-4" />
          <AlertDescription>
            This candidate did not opt-in to SMS messaging. Messages cannot be sent.
          </AlertDescription>
        </Alert>
      </template>
    </SheetBody>
  </SheetContent>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from 'vue-sonner';
import type { ContactMessageEntity } from '@/api/entity_types/ContactMessage';
import { SheetContent, SheetHeader, SheetTitle, SheetBody } from '@/components/ui/sheet';
import ChatHistory from './ChatHistory.vue';
import ChatInput from '../ChatInput.vue';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { useCableChannel } from '@/composables/useCableChannel';
import { Api } from '@/api';
import LocalLoadingSpinner from '@/components/shared/LocalLoadingSpinner.vue';

const loadingSpinnerStore = useLoadingSpinnerStore();

export type ContactSheetContentProps = {
  name: string;
  id: number;
  canReceiveSms: boolean;
};

const props = defineProps<ContactSheetContentProps>();

const inputMessage = ref<string>('');
const isScheduled = ref<boolean>(false);
const invokeOn = ref<string>('');

const contactMessages = ref<ContactMessageEntity[]>([]);
const isLoading = ref<boolean>(true);

const fetchContactMessages = async (): Promise<void> => {
  const response = await Api.Contacts.getContactMessages(props.id);

  isLoading.value = false;

  if (response.status === 'success') {
    contactMessages.value = response.data.reverse();
  } else {
    toast.error(response.messages.join(', '));
  }
};

const scheduleMessage = async (): Promise<void> => {
  loadingSpinnerStore.showLocalLoadingSpinner();

  try {
    const response = await Api.ScheduledActions.createScheduledMessage({
      contactId: props.id,
      invokeOn: invokeOn.value,
      body: inputMessage.value,
    });

    loadingSpinnerStore.hideLocalLoadingSpinner();

    if (response.status === 'success') {
      inputMessage.value = '';
      invokeOn.value = '';
      isScheduled.value = false;
      toast.success('Message scheduled', {
        description: 'Your message was successfully scheduled for this contact.',
      });
    } else {
      toast.error('Message scheduling error', {
        description: response.messages.join(', '),
      });
    }
  } catch {
    loadingSpinnerStore.hideLocalLoadingSpinner();
    toast.error('Message scheduling error', {
      description: 'An unexpected error occurred.',
    });
  }
};

const sendMessage = async (): Promise<void> => {
  const messageBody = inputMessage.value;
  inputMessage.value = '';

  const response = await Api.Contacts.sendContactMessage({
    contactId: props.id,
    content: messageBody,
  });

  if (response.status === 'success') {
    toast.success('Message sent');
    await fetchContactMessages();
  } else {
    toast.error('Contact message error', {
      description: response.messages.join(', '),
    });
  }
};

useCableChannel<{ new_message: ContactMessageEntity | null }>({
  channel: 'ContactMessagesChannel',
  onMessage: data => {
    if (data.new_message && data.new_message.contact_id == props.id) {
      contactMessages.value = [data.new_message, ...contactMessages.value];
    }
  },
});

onMounted(async () => {
  loadingSpinnerStore.showLoadingSpinner();
  await fetchContactMessages();
  loadingSpinnerStore.hideLoadingSpinner();
});
</script>
